import React, { useEffect, useState } from "react";
import { STATUS_CODES } from "../../utils/statusCode";
import Toster from "../../utils/Toaster";
import {
  handleAdminUserDetail,
  handleAdminUserPlan,
  handleAdminUserSubscribe,
} from "../../services/APIClient";
import { useNavigate, useParams } from "react-router-dom";
import { removeUserData } from "../../store/slices/userSlice";
import { useDispatch } from "react-redux";
import { useLoader } from "../../contexts/LoaderContext";
import { IoArrowBack } from "react-icons/io5";

const OfficerDetail = () => {
  const [userDetail, setUserDetail] = useState({});
  const [planDetails, setPlanDetails] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLoading } = useLoader();

  const fetchUserPlan = async (userId) => {
    setLoading(true);
    try {
      const result = await handleAdminUserPlan(userId);
      if (result?.code === 200) {
        setPlanDetails(result?.data);
      } else {
        console.error("Failed to fetch plan details");
      }
    } catch (error) {
      console.error("Error fetching plan details", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async () => {
    setLoading(true);
    try {
      const response = await handleAdminUserDetail({ id }, id);
      if (response?.code === STATUS_CODES.SUCCESS) {
        setUserDetail(response?.data?.userDetails);
        await fetchUserPlan(id);
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to fetch user detail", "error");
      }
    } catch (error) {
      Toster("An error occurred while fetching user details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchUserDetail();
  }, [id]);

  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  const handleSubscribePlan = async (planId) => {
    setLoading(true);
    try {
      const response = await handleAdminUserSubscribe(id, planId);
      Toster("Subscribed successfully", "success");
      setIsModalOpen(false);
      await fetchUserDetail();
    } catch (error) {
      Toster(error.message || "Subscription failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-center">
        <div
          className="bg-white shadow-md rounded-lg overflow-hidden w-full max-w-sm sm:max-w-md md:max-w-lg"
          style={{ maxHeight: "530px" }}
        >
          {/* Background with Back Button */}
          <div className="relative h-28 bg-cover bg-center break-words">
            <button
              onClick={() => navigate("/officers")}
              className="absolute top-3 left-3  bg-white text-[#124389] px-2 py-1 rounded-full flex items-center gap-1 text-sm font-medium shadow"
            >
              <IoArrowBack className="text-lg" />
              Back
            </button>

            <img
              src={`${process.env.PUBLIC_URL}/bg_user_img.png`}
              alt="Background"
              className="w-full h-[140px] object-cover"
            />
          </div>

          <div className="p-6 relative -mt-12">
            <div className="flex flex-row items-center mb-4 sm:mb-6 gap-4 min-w-[320px] flex-wrap sm:flex-nowrap">
              <img
                src={userDetail.profileImage}
                alt="Profile"
                className="w-24 h-24 rounded-full border-4 border-white object-cover"
              />
              <div className=" flex flex-wrap gap-2 ">
                <button className="bg-[#4F8FED] text-white px-2 min-w-[120px] h-[30px] rounded break-words">
                  {userDetail.userSubscribedPlan || "Not Subscribed"}
                </button>
                {planDetails && planDetails.isSubscribed === 0 && (
                  <button
                    className="bg-red-500 text-white px-2 min-w-[120px] h-[30px] rounded break-words"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Subscribe
                  </button>
                )}
              </div>
            </div>

            <div className="text-left ml-0 sm:ml-1">
              <h2 className="text-2xl text-[#252525] font-semibold break-words">
                {userDetail.fullname}
              </h2>
              <p className="text-sm break-words text-[#515A6B]">
                {userDetail.badgeNumber}
              </p>
            </div>

            {/* <div className="w-full border-t mt-4 mb-4" /> */}

            <div className="text-left space-y-3">
              {/* <div>
                <p className="text-sm text-[#515A6B] font-medium break-words">
                  User ID
                </p>
                <p className="text-sm break-words text-[#2A3B58]">
                  {userDetail.userUniqueId}
                </p>
              </div> */}

              <div className="w-full border-t" />

              <div>
                <p className="text-sm text-[#515A6B] font-medium break-words">
                  Email
                </p>
                <p className="text-sm break-words text-[#2A3B58]">
                  {userDetail.email}
                </p>
              </div>

              {userDetail.departmentName && (
                <>
                  <div className="w-full border-t" />
                  <div>
                    <p className="text-sm text-[#515A6B] font-medium break-words">
                      Department
                    </p>
                    <p className="text-sm break-words text-[#2A3B58]">
                      {userDetail.departmentName}
                    </p>
                  </div>
                </>
              )}

              {userDetail.dialCode && userDetail.phoneNumber && (
                <>
                  <div className="w-full border-t" />
                  <div>
                    <p className="text-sm text-[#515A6B] font-medium">
                      Phone number
                    </p>
                    <p className="text-sm text-[#2A3B58]">
                      {`${userDetail.dialCode} ${userDetail.phoneNumber}`}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-7xl p-6 relative overflow-y-auto max-h-[90vh]">
            <button
              className="absolute top-2 right-2 text-gray-600 hover:text-black text-2xl font-bold"
              onClick={() => setIsModalOpen(false)}
              aria-label="Close modal"
            >
              &times;
            </button>

            <h2 className="text-2xl font-semibold text-[#2A3B58] mb-6 text-center">
              Select a Subscription Plan
            </h2>

            <div className="flex flex-wrap justify-center gap-6 ">
              {planDetails?.plans
                ?.filter((item) => item.planName.toLowerCase() !== "free trial")
                .map((item, index) => (
                  <div
                    key={index}
                    className="max-w-[300px] flex flex-col justify-between w-full bg-white rounded-[10px] pt-[25px] pb-[16px] shadow-md border"
                  >
                    <div>
                      <div className="py-[15px] w-full bg-[#124389] text-white text-center font-bold text-xl rounded-t-[10px]">
                        {item.planName}
                      </div>
                      <div className="mt-[20px] px-[16px] pb-[20px] text-center">
                        <h2 className="font-bold text-[32px] text-[#252525]">
                          ${item.price}{" "}
                          <span className="text-[16px] text-[#888] font-medium">
                            /{item.planFrequencyType}
                          </span>
                        </h2>
                        <div className="flex flex-col gap-3 mt-6 text-left">
                          {item.planBenefits?.map((content, idx) => (
                            <div className="flex items-start gap-2" key={idx}>
                              <span className="text-blue-500 mt-1">✔️</span>
                              <p className="text-sm text-[#152330]">
                                {content}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="px-[16px]">
                      <button
                        type="button"
                        className="mt-6 w-full rounded-[70px] bg-[#353B49] text-white py-2 font-bold text-[18px]"
                        onClick={() => handleSubscribePlan(item._id)}
                      >
                        {item.planName !== "Pay Per Use"
                          ? "subscribe"
                          : "Buy Now"}
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfficerDetail;
